/**
 * `PaidMediaConnectTikTokAdsCard` — Task 204 (IC-8 FE wiring).
 *
 * The TikTok sibling of `PaidMediaConnectMetaAdsCard` /
 * `PaidMediaConnectSnapchatAdsCard`. Drives two surfaces on the client
 * paid-media tab:
 *
 * 1. **Connect CTA** — bounces the user to the TikTok-hosted authorize
 *    page via the IC-3/IC-8 `start-integration-connection-oauth` EF with
 *    `provider: 'tiktok'`. Strict gating, identical posture to the Meta
 *    and Snapchat cards and aligned with the server gates:
 *      0. `env.ic8TikTokOauthUiEnabled` — build-time flag, default OFF.
 *      1. `me` loaded.
 *      2. `hasOnlyClientCategoryRoles(me)` — strict client-only (reused
 *         from the Meta card so the cards stay identical).
 *      3. `hasClientAssignment(me, clientId)` — active assignment.
 *
 * 2. **Connected state** — when the parent passes an `accounts` list that
 *    contains an active `tiktok_ads` ad_account linked to an
 *    integration_connection for `clientId`, render a quiet "متصل" badge
 *    INSTEAD of the CTA. Gated on the active assignment (NOT the
 *    OAuth-write flag) — an existing connection deserves to be observable.
 *
 * Hard rules (this component MUST NOT):
 *   - Call the TikTok Marketing API directly. All TikTok API calls happen
 *     server-side inside `complete-integration-connection-oauth`.
 *   - Write to `ad_accounts`. The card only READS the passed-in list.
 *   - Touch `social_connections`, the publishing OAuth surface, or any
 *     ad/campaign mutation. Phase 1 is connect-only.
 */

import { useCallback, useState } from 'react';
import { isApiError } from '../../api/client';
import { startIntegrationConnectionOAuth } from '../../api/endpoints';
import { env } from '../../env';
import { hasClientAssignment } from '../../permissions';
import { useMeContext } from '../../context/MeContextProvider';
import {
  describeStartOAuthError,
  hasOnlyClientCategoryRoles,
  IC3_REDIRECT_BACK_TO_SESSION_KEY_PREFIX,
  IC_OAUTH_PROVIDER_SESSION_KEY_PREFIX,
} from './PaidMediaConnectMetaAdsCard';
import type { AdAccountRow, MeContext } from '../../api/types';

/**
 * Single end-to-end gate function (mirrors the Meta card). The component
 * returns `null` iff this returns `false`. Exported for direct test access.
 *
 * Guards in order: build-time flag → me loaded → strict client-only role
 * posture (matches the IC-3 EF gate exactly) → active client-assignment.
 */
export function shouldRenderConnectTikTokAdsCard(
  me: MeContext | null,
  clientId: string,
  enabled: boolean,
): boolean {
  if (!enabled) return false;
  if (!me) return false;
  if (!hasOnlyClientCategoryRoles(me)) return false;
  if (!hasClientAssignment(me, clientId)) return false;
  return true;
}

/**
 * True iff `accounts` contains an active, non-archived `tiktok_ads`
 * ad-account for `clientId` that carries a string-truthy
 * `integration_connection_id` (a real linked credential). Pure helper;
 * exported for direct test access.
 */
export function hasActiveLinkedTikTokAccount(
  accounts: ReadonlyArray<AdAccountRow>,
  clientId: string,
): boolean {
  return accounts.some((a) => isLinkedActiveTikTokRow(a, clientId));
}

function isLinkedActiveTikTokRow(a: AdAccountRow, clientId: string): boolean {
  return (
    a.client_id === clientId &&
    a.platform === 'tiktok_ads' &&
    a.status === 'active' &&
    a.archived_at === null &&
    typeof a.integration_connection_id === 'string' &&
    a.integration_connection_id.length > 0
  );
}

function findActiveLinkedTikTokAccount(
  accounts: ReadonlyArray<AdAccountRow>,
  clientId: string,
): AdAccountRow | null {
  return accounts.find((a) => isLinkedActiveTikTokRow(a, clientId)) ?? null;
}

/**
 * A PRESENT (string) backing integration_connection status that is not
 * `active` (reauthorization_required / revoked / expired). Mirrors the
 * Meta card's Task 152 helper. A missing / undefined / null status never
 * counts — it degrades safely during a wire-version skew.
 */
function isKnownNonActiveStatus(a: AdAccountRow): boolean {
  const status = a.integration_connection_status;
  return typeof status === 'string' && status !== 'active';
}

/**
 * EXISTENTIAL: true iff AT LEAST ONE linked active tiktok_ads account
 * for `clientId` has a backing integration_connection that is `active`
 * (the state that makes the green "متصل" badge eligible). Checking EVERY
 * matching row (not just the first) means a sibling row can't mask the
 * answer. Exported for direct test access.
 */
export function isLinkedTikTokConnectionActive(
  accounts: ReadonlyArray<AdAccountRow>,
  clientId: string,
): boolean {
  return accounts.some(
    (a) => isLinkedActiveTikTokRow(a, clientId) && a.integration_connection_status === 'active',
  );
}

/**
 * EXISTENTIAL: true iff AT LEAST ONE linked active tiktok_ads account
 * for `clientId` has a backing integration_connection whose status is a
 * KNOWN non-active value (reauthorization_required / revoked / expired).
 * A missing / undefined / null status does NOT trigger this — it falls
 * through to the connect gate rather than showing a false "re-link"
 * prompt during a wire-version skew. Exported for direct test access.
 */
export function hasReauthRequiredTikTokAccount(
  accounts: ReadonlyArray<AdAccountRow>,
  clientId: string,
): boolean {
  return accounts.some((a) => isLinkedActiveTikTokRow(a, clientId) && isKnownNonActiveStatus(a));
}

function findReauthRequiredTikTokAccount(
  accounts: ReadonlyArray<AdAccountRow>,
  clientId: string,
): AdAccountRow | null {
  return (
    accounts.find((a) => isLinkedActiveTikTokRow(a, clientId) && isKnownNonActiveStatus(a)) ?? null
  );
}

interface ConnectState {
  readonly status: 'idle' | 'loading' | 'error';
  readonly errorCode?: string;
  readonly errorMessageAr?: string;
}

interface PaidMediaConnectTikTokAdsCardProps {
  readonly clientId: string;
  readonly accounts?: ReadonlyArray<AdAccountRow>;
}

export function PaidMediaConnectTikTokAdsCard({
  clientId,
  accounts = [],
}: PaidMediaConnectTikTokAdsCardProps) {
  const { me } = useMeContext();
  const [connect, setConnect] = useState<ConnectState>({ status: 'idle' });

  const handleConnect = useCallback(async () => {
    setConnect({ status: 'loading' });
    try {
      const redirectBackTo =
        typeof window !== 'undefined' ? window.location.pathname + window.location.search : null;

      const data = await startIntegrationConnectionOAuth({
        input: {
          client_id: clientId,
          provider: 'tiktok',
          redirect_back_to: redirectBackTo,
        },
      });

      try {
        if (typeof window !== 'undefined' && data.state && redirectBackTo) {
          window.sessionStorage.setItem(
            `${IC3_REDIRECT_BACK_TO_SESSION_KEY_PREFIX}${data.state}`,
            redirectBackTo,
          );
        }
        // Task 191 — stash the provider so the shared callback page can
        // render provider-aware copy (TikTok, not the hardcoded Meta).
        if (typeof window !== 'undefined' && data.state) {
          window.sessionStorage.setItem(
            `${IC_OAUTH_PROVIDER_SESSION_KEY_PREFIX}${data.state}`,
            'tiktok',
          );
        }
      } catch {
        // SessionStorage failures (private mode, quota) are non-fatal.
      }

      window.location.href = data.authorize_url;
    } catch (err: unknown) {
      if (isApiError(err)) {
        const fieldMarker = readFirstFieldMarker(err.body.fields);
        const wireDetail = fieldMarker ?? err.code;
        setConnect({
          status: 'error',
          errorCode: wireDetail,
          errorMessageAr: describeStartOAuthError(wireDetail),
        });
        return;
      }
      setConnect({
        status: 'error',
        errorCode: 'network_error',
        errorMessageAr: 'تعذّر الاتصال بالخدمة. تحقّق من اتصالك بالإنترنت وحاول مرة أخرى.',
      });
    }
  }, [clientId]);

  // Reauthorization-required branch — checked BEFORE the green connected
  // branch so a broken connection always wins over a healthy sibling
  // (mirrors the Meta card's Task 152). A linked active tiktok_ads
  // ad_account exists for this workspace but its backing
  // integration_connection is no longer `active` (reauthorization_required
  // / revoked / expired). Render a warning — NEVER the green "متصل" badge —
  // and keep the account visible. The WARNING is gated on the active
  // assignment (NOT the OAuth-write flag); the re-link BUTTON is gated on
  // the same full client-only predicate the connect CTA uses. MUST sit
  // AFTER every hook call (Rules of Hooks).
  if (
    me !== null &&
    hasClientAssignment(me, clientId) &&
    hasReauthRequiredTikTokAccount(accounts, clientId)
  ) {
    const account = findReauthRequiredTikTokAccount(accounts, clientId);
    return (
      <div className="card" style={{ padding: 22 }}>
        <div className="sec-title">
          <h2>إعادة ربط حساب إعلانات TikTok</h2>
          <span className="pill pill-review">إعادة الربط مطلوبة</span>
        </div>
        <p className="tiny" style={{ color: 'var(--muted)', marginTop: 6 }}>
          {account?.account_name ? (
            <>
              انتهت صلاحية ربط الحساب <strong>{account.account_name}</strong>. أعد ربط الحساب
              لاستئناف قراءة بيانات الحملات. لن يتأثر أي ربط آخر.
            </>
          ) : (
            <>انتهت صلاحية ربط حساب إعلانات TikTok. أعد الربط لاستئناف قراءة بيانات الحملات.</>
          )}
        </p>
        {shouldRenderConnectTikTokAdsCard(me, clientId, env.ic8TikTokOauthUiEnabled) ? (
          <div style={{ marginTop: 16 }}>
            <button
              type="button"
              className="btn-primary"
              onClick={handleConnect}
              disabled={connect.status === 'loading'}
            >
              {connect.status === 'loading' ? 'جاري التحويل إلى TikTok…' : 'إعادة ربط الحساب'}
            </button>
          </div>
        ) : (
          <p className="tiny" style={{ color: 'var(--muted)', marginTop: 12 }}>
            إعادة الربط تتم من حساب العميل.
          </p>
        )}
        {connect.status === 'error' ? (
          <div
            className="banner banner-error"
            role="alert"
            style={{ marginTop: 12 }}
            data-error-code={connect.errorCode}
          >
            {connect.errorMessageAr}
          </div>
        ) : null}
      </div>
    );
  }

  // Connected-state branch — green "متصل" ONLY when a linked active
  // tiktok_ads account exists AND its backing integration_connection is
  // `active` (Task 152 parity). MUST sit AFTER every hook call.
  if (
    me !== null &&
    hasClientAssignment(me, clientId) &&
    hasActiveLinkedTikTokAccount(accounts, clientId) &&
    isLinkedTikTokConnectionActive(accounts, clientId)
  ) {
    const active = findActiveLinkedTikTokAccount(accounts, clientId);
    return (
      <div className="card" style={{ padding: 22 }}>
        <div className="sec-title">
          <h2>تم ربط حساب إعلانات TikTok</h2>
          <span className="pill pill-active">متصل</span>
        </div>
        <p className="tiny" style={{ color: 'var(--muted)', marginTop: 6 }}>
          {active?.account_name ? (
            <>
              الحساب: <strong>{active.account_name}</strong>
              {active?.currency_code ? <> · {active.currency_code}</> : null}
            </>
          ) : (
            <>تمت قراءة بيانات الحملات أدناه.</>
          )}
        </p>
      </div>
    );
  }

  if (!shouldRenderConnectTikTokAdsCard(me, clientId, env.ic8TikTokOauthUiEnabled)) return null;

  return (
    <div className="card" style={{ padding: 22 }}>
      <div className="sec-title">
        <h2>اربط حساب إعلانات TikTok</h2>
      </div>
      <p className="tiny" style={{ color: 'var(--muted)', marginTop: 6 }}>
        اربط حساب إعلاناتك على TikTok لقراءة بيانات الحملات. سنطلب صلاحية{' '}
        <code style={{ direction: 'ltr', unicodeBidi: 'isolate' }}>tiktok-marketing-api</code>{' '}
        للقراءة فقط — لا نقوم بإنشاء أو تعديل أي إعلان أو حملة.
      </p>
      <div style={{ marginTop: 16 }}>
        <button
          type="button"
          className="btn-primary"
          onClick={handleConnect}
          disabled={connect.status === 'loading'}
        >
          {connect.status === 'loading' ? 'جاري التحويل إلى TikTok…' : 'اربط الحساب الآن'}
        </button>
      </div>
      {connect.status === 'error' ? (
        <div
          className="banner banner-error"
          role="alert"
          style={{ marginTop: 12 }}
          data-error-code={connect.errorCode}
        >
          {connect.errorMessageAr}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Inspect a `KmiApiError.body.fields` map and return the first `_body[0]`
 * marker if present, or any other field's first value. Local copy of the
 * Meta card's helper (not exported there) to keep the cards decoupled.
 */
function readFirstFieldMarker(
  fields: Readonly<Record<string, ReadonlyArray<string>>> | undefined,
): string | undefined {
  if (!fields) return undefined;
  const bodyMarkers = fields['_body'];
  if (bodyMarkers && bodyMarkers.length > 0) return bodyMarkers[0];
  for (const v of Object.values(fields)) {
    if (v && v.length > 0) return v[0];
  }
  return undefined;
}
